import { useEffect, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, storage } from '../../config/firebaseConfig';
import { PRIMARY_ADDRESS_HOME } from '../../utils/employeeAddress';
import { getComplaintTargets } from './complaints/complaintOptions';

export const EMPTY_USER_DATA = {
  eName: '',
  eSurname: '',
  role: '',
  phone: '',
  emergencyPhone: '', // Acil durum telefonu
  eMail: '',
  address: '', // Ev adresi (alan adı geriye dönük uyumluluk için 'address')
  workAddress: '',
  primaryAddress: PRIMARY_ADDRESS_HOME, // Hangi adres "ana adres"
  annualPermit: '',
  projects: [],
};

/**
 * The signed-in employee's own record (live), the colleague list the
 * messenger needs, the unread message count and whether the user is an admin
 * or one of the people petitions are sent to.
 */
const useProfileData = ({ setProfileImageUrl }) => {
  const [userData, setUserData] = useState(EMPTY_USER_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isComplaintTarget, setIsComplaintTarget] = useState(false);

  useEffect(() => {
    let unsubscribeUserData = null;

    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        // Profil fotoğrafı
        const imageRef = ref(storage, `profileImages/${user.uid}.jpg`);
        try {
          const url = await getDownloadURL(imageRef);
          setProfileImageUrl(url);
        } catch (imageError) {
          // Fotoğraf okunamasa da profil bilgileri yüklenmeye devam eder
          if (imageError.code !== 'storage/object-not-found') console.error('Profile image could not be loaded:', imageError);
        }

        // Çalışan kaydı, canlı dinleyici ile
        const q = query(collection(db, 'Employees'), where('eMail', '==', user.email));
        unsubscribeUserData = onSnapshot(q, async (querySnapshot) => {
          if (querySnapshot.empty) {
            console.log('No such employee!');
            setLoading(false);
            return;
          }
          const data = querySnapshot.docs[0].data();

          // Projelerin kendi durumu (Aktif/Pasif) ve tasarımı (çift kör / open label /
          // hibrit) Projects koleksiyonundan gelir
          let enrichedProjects = data.projects || [];
          if (enrichedProjects.length > 0) {
            try {
              const projectIds = [...new Set(enrichedProjects.map((p) => p.id).filter(Boolean))];
              if (projectIds.length > 0) {
                const projectsSnapshot = await getDocs(collection(db, 'Projects'));
                const projectInfoMap = {};
                projectsSnapshot.docs.forEach((projectDoc) => {
                  const projectData = projectDoc.data();
                  projectInfoMap[projectDoc.id] = {
                    projectStatus: projectData.projectStatus || 'Aktif',
                    projectDesign: projectData.projectDesign || '',
                  };
                });
                enrichedProjects = enrichedProjects.map((project) => ({
                  ...project,
                  projectStatus: project.id ? projectInfoMap[project.id]?.projectStatus : 'Aktif',
                  projectDesign: (project.id && projectInfoMap[project.id]?.projectDesign) || '',
                }));
              }
            } catch (projectError) {
              console.error('Error fetching project status:', projectError);
            }
          }

          setUserData({
            eName: data.eName || '',
            eSurname: data.eSurname || '',
            role: data.role || '',
            phone: data.phone || '',
            emergencyPhone: data.emergencyPhone || '',
            eMail: data.eMail || '',
            address: data.address || '',
            workAddress: data.workAddress || '',
            primaryAddress: data.primaryAddress || PRIMARY_ADDRESS_HOME,
            annualPermit: data.annualPermit ?? '',
            projects: enrichedProjects,
          });
          setLoading(false);
        }, (listenError) => {
          console.error('Error in real-time listener: ', listenError);
          setError('load');
          setLoading(false);
        });
      } catch (fetchError) {
        console.error('Error fetching user data: ', fetchError);
        setError('load');
        setLoading(false);
      }
    };

    // Mesajlaşma için aktif çalışanlar; fotoğrafı kayıtta olmayanlar arka planda yüklenir
    const fetchEmployeesFromFirestore = async () => {
      try {
        const user = auth.currentUser;
        const querySnapshot = await getDocs(collection(db, 'Employees'));
        const employeesData = querySnapshot.docs.map((employeeDoc) => {
          const data = employeeDoc.data();
          return { ...data, id: employeeDoc.id, profileImageUrl: data.profileImageUrl || null };
        });

        const filteredEmployees = employeesData
          .filter((empData) => empData.eMail !== user.email && empData.Active === 1)
          .sort((a, b) => (a.eName || '').toLowerCase().localeCompare((b.eName || '').toLowerCase(), 'tr'));

        setUsers(filteredEmployees);

        const employeesWithoutImages = filteredEmployees.filter((emp) => !emp.profileImageUrl && emp.eMail);
        if (employeesWithoutImages.length > 0) {
          const allImageResults = [];
          const batchSize = 5;
          for (let i = 0; i < employeesWithoutImages.length; i += batchSize) {
            const batch = employeesWithoutImages.slice(i, i + batchSize);
            // eslint-disable-next-line no-await-in-loop
            const results = await Promise.all(batch.map(async (emp) => {
              const paths = [`profileImages/${emp.id}.jpg`, `profileImages/${emp.eMail}.jpg`];
              for (const path of paths) {
                try {
                  // eslint-disable-next-line no-await-in-loop
                  const url = await getDownloadURL(ref(storage, path));
                  return { id: emp.id, profileImageUrl: url };
                } catch (err) {
                  // sıradaki yolu dene
                }
              }
              return null;
            }));
            allImageResults.push(...results.filter(Boolean));
          }
          // Tek güncelleme (her fotoğrafta yeniden çizilmesin)
          if (allImageResults.length > 0) {
            setUsers((prev) => prev.map((emp) => {
              const found = allImageResults.find((r) => r.id === emp.id);
              return found ? { ...emp, profileImageUrl: found.profileImageUrl } : emp;
            }));
          }
        }
      } catch (employeesError) {
        console.error('Error fetching employees: ', employeesError);
      }
    };

    fetchUserData();
    fetchEmployeesFromFirestore();

    return () => {
      if (unsubscribeUserData) unsubscribeUserData();
    };
  }, [setProfileImageUrl]);

  // Okunmamış mesaj sayısı (sunucuda süzülür)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return undefined;
    const unreadMessagesQuery = query(
      collection(db, 'messages'),
      where('receiverEmail', '==', user.email),
      where('read', '==', false)
    );
    return onSnapshot(unreadMessagesQuery, (snapshot) => setTotalUnreadMessages(snapshot.size));
  }, []);

  // Admin mi, dilekçe muhatabı mı?
  useEffect(() => {
    const targetEmails = getComplaintTargets().map((target) => target.email.toLowerCase());

    const checkUserRole = async (user) => {
      if (targetEmails.includes(user.email.toLowerCase())) setIsComplaintTarget(true);
      try {
        const usersRef = collection(db, 'Users');
        const [userDocSnap, emailSnapshot, eMailSnapshot] = await Promise.all([
          getDoc(doc(db, 'Users', user.uid)),
          getDocs(query(usersRef, where('email', '==', user.email))),
          getDocs(query(usersRef, where('eMail', '==', user.email))),
        ]);
        if (
          (userDocSnap.exists() && userDocSnap.data().role === 'admin')
          || (!emailSnapshot.empty && emailSnapshot.docs[0].data().role === 'admin')
          || (!eMailSnapshot.empty && eMailSnapshot.docs[0].data().role === 'admin')
        ) {
          setIsUserAdmin(true);
        }
      } catch (roleError) {
        console.error('Error checking user role:', roleError);
      }
    };

    return onAuthStateChanged(auth, (user) => {
      if (user) {
        checkUserRole(user);
      } else {
        setIsUserAdmin(false);
        setIsComplaintTarget(false);
      }
    });
  }, []);

  return { userData, setUserData, loading, error, users, totalUnreadMessages, isUserAdmin, isComplaintTarget };
};

export default useProfileData;
